import React from 'react';
import { Exam } from '../../types/exam';

interface StudentExamCardProps {
  exam: Exam;
  onStart: (exam: Exam) => void;
}

export const StudentExamCard: React.FC<StudentExamCardProps> = ({ exam, onStart }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isExamActive = () => {
    const now = new Date();
    const startDate = new Date(exam.startDate);
    const endDate = new Date(exam.endDate);
    return now >= startDate && now <= endDate;
  };

  const isExamUpcoming = () => {
    const now = new Date();
    const startDate = new Date(exam.startDate);
    return now < startDate;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4">
        <h3 className="text-lg font-bold mb-1">{exam.title}</h3>
        <p className="text-sm text-blue-100">{exam.subject}</p>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Description */}
        {exam.description && (
          <p className="text-sm text-gray-600 line-clamp-2">{exam.description}</p>
        )}

        {/* Key Info */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="bg-gray-50 p-2 rounded">
            <p className="text-gray-600">Total Marks</p>
            <p className="font-bold text-gray-800">{exam.totalMarks}</p>
          </div>
          <div className="bg-gray-50 p-2 rounded">
            <p className="text-gray-600">Duration</p>
            <p className="font-bold text-gray-800">{exam.duration} min</p>
          </div>
          <div className="bg-gray-50 p-2 rounded">
            <p className="text-gray-600">Questions</p>
            <p className="font-bold text-gray-800">{exam.totalQuestions}</p>
          </div>
          <div className="bg-gray-50 p-2 rounded">
            <p className="text-gray-600">Passing %</p>
            <p className="font-bold text-gray-800">{exam.passingPercentage}%</p>
          </div>
        </div>

        {/* Dates */}
        <div className="border-t pt-3">
          <p className="text-xs text-gray-500 mb-2">
            <strong>Starts:</strong> {formatDate(exam.startDate)}
          </p>
          <p className="text-xs text-gray-500">
            <strong>Ends:</strong> {formatDate(exam.endDate)}
          </p>
        </div>

        {/* Status Badge */}
        <div className="flex items-center gap-2">
          {isExamActive() && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              Active Now
            </span>
          )}
          {isExamUpcoming() && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
              <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
              Coming Soon
            </span>
          )}
          {exam.status === 'CLOSED' && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-800 text-xs font-semibold rounded-full">
              <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
              Closed
            </span>
          )}
        </div>
      </div>

      {/* Footer - Action Button */}
      <div className="border-t bg-gray-50 p-4">
        <button
          onClick={() => onStart(exam)}
          disabled={!isExamActive()}
          className={`w-full py-2 px-4 rounded font-medium transition ${
            isExamActive()
              ? 'bg-blue-500 hover:bg-blue-600 text-white cursor-pointer'
              : 'bg-gray-300 text-gray-600 cursor-not-allowed'
          }`}
          title={
            !isExamActive()
              ? isExamUpcoming()
                ? 'Exam not yet started'
                : 'Exam has ended'
              : 'Start taking exam'
          }
        >
          {isExamActive() ? 'Start Exam' : 'Not Available'}
        </button>
      </div>
    </div>
  );
};

export default StudentExamCard;
